import React                                         from 'react';
import {
  isNullOrUndefinedOrEmpty,
  UTC,
}                                                    from 'rewire-common';
import {
  observable,
  createWatcherFn,
  WatcherTypeFn,
  freeze,
}                                                    from 'rewire-core';
import {
  Form,
  IFormContext,
}                                                    from 'rewire-ui';
import SearchIcon                                    from '@material-ui/icons/SearchOutlined';
import EditIcon                                      from '@material-ui/icons/EditOutlined';
import VisibilityIcon                                from '@material-ui/icons/Visibility';
import {
  IEmployee,
  FieldFilter,
  ScheduleFilterInput,
  IScheduleRuleContext,
  INameLike,
}                                                    from '../DataTypes';
import mainPageModel                                 from '../main-navigation/MainPageModel';
import {
  getNameSearcher,
}                                                    from '../utility/Searchers';
import scheduleRuleDialogModel, {
  ISchedulingRuleDialogData,
  getScheduleRuleQuery,
}                                                    from './ScheduleRuleDialogModel';
import infoPanelShiftSchedulingViewModel             from './InfoPanelShiftSchedulingViewModel';
import DataQueries                                   from '../graphql/Queries';
import {
  AutoSchedulePropertyMap
}                                                    from './AutoScheduleQueryMapper';
import {
  AdvancedAutoCompleteActionType,
  AdvancedAutoCompleteSuggestionActionType,
  getAdvancedFormAutoComplete
}                                                    from '../ui/AdvancedAutoComplete';

interface IInfoPanelShiftSchedulingHeaderModelState {
  scheduleRuleQuery?: string;
}

export class InfoPanelShiftSchedulingHeaderViewModel {
  state:                IInfoPanelShiftSchedulingHeaderModelState;
  form:                 Form;
  watchControlSettings: WatcherTypeFn;

  constructor() {
    this.state = observable({
      scheduleRuleQuery: undefined,
    });

    const employeeSearcher             = getNameSearcher('employees');
    const schedulingRuleSearcherFilter = () => ({field: 'type', eq: 'schedulingRule'} as FieldFilter);
    const schedulingRuleSearcher       = getNameSearcher('views', schedulingRuleSearcherFilter);
    const schedulingRuleAction:           AdvancedAutoCompleteActionType           = {action: () => this.showScheduleRuleDialog(), label: 'Add Scheduling Rule', disabled: () => this.viewOnly};
    const schedulingRuleSuggestionAction: AdvancedAutoCompleteSuggestionActionType = {action: (schedulingRuleId: string) => this.showScheduleRuleDialog(schedulingRuleId), icon: !this.viewOnly ? EditIcon : VisibilityIcon};

    this.form                          = Form.create((form: IFormContext) => ({
      employeeSearch: form.reference(employeeSearcher).label('Employee Quick Search').placeholder('enter an employee').disableErrors().onValueChange(this.handleEmployeeSearchChange).disabled(() => !!this.schedulingRule).startAdornment(() => React.createElement(SearchIcon, undefined, undefined)),
      schedulingRule: getAdvancedFormAutoComplete(form, schedulingRuleSearcher, 'Scheduling Rule', schedulingRuleAction, schedulingRuleSuggestionAction).label('Scheduling Rule').placeholder('enter a scheduling rule').disableErrors().onValueChange(this.handleSchedulingRuleFieldChange).disabled(() => !isNullOrUndefinedOrEmpty(this.employeeSearch)),
    }),
    {},
    {
      variant:          'outlined',
      updateOnChange:   true,
      validateOnUpdate: false
    });

    this.watchControlSettings = createWatcherFn();
  }

  reset() {
    this.watchControlSettings();
    this.state.scheduleRuleQuery = undefined;
  }

  load = async () => {
    this.watchControlSettings(() => { this.scheduleRuleQuery; }, async () => {
      await infoPanelShiftSchedulingViewModel.loadSchedulingGrid();
    });
  };

  get scheduleRuleQuery(): string | undefined {
    return this.state.scheduleRuleQuery;
  }

  set scheduleRuleQuery(scheduleRuleQuery: string | undefined) {
    this.state.scheduleRuleQuery = scheduleRuleQuery;
  }

  get scheduleRuleContext(): IScheduleRuleContext | undefined {
    return infoPanelShiftSchedulingViewModel.scheduleRuleContext;
  }

  get employeeSearch(): IEmployee | undefined {
    return this.form.field.employeeSearch.value;
  }

  get schedulingRule(): INameLike | undefined {
    return this.form.field.schedulingRule.value;
  }

  set schedulingRule(value: INameLike | undefined) {
    this.form.setFieldValue('schedulingRule', value);
  }

  get viewOnly(): boolean {
    return mainPageModel.scheduleViewOnly;
  }

  get earnedDate(): UTC {
    return infoPanelShiftSchedulingViewModel.earnedDate;
  }

  get autoSchedulePropertyMap(): AutoSchedulePropertyMap {
    return scheduleRuleDialogModel.autoSchedulePropertyMap;
  }

  showScheduleRuleDialog(schedulingRuleId?: string) {
    scheduleRuleDialogModel.load({scheduleRuleContext: this.scheduleRuleContext, scheduleRuleId: schedulingRuleId, earnedDate: this.earnedDate, disableDate: true, onSave: this.scheduleRuleDialogOnSave, onDelete: this.scheduleRuleDialogOnDelete});
  }

  scheduleRuleDialogOnSave = (schedulingRule?: ISchedulingRuleDialogData) => {
    if (this.schedulingRule && this.schedulingRule.id === schedulingRule?.id) {
      freeze(() => {
        this.schedulingRule    = {id: schedulingRule.id!, name: schedulingRule.name!};
        this.scheduleRuleQuery = schedulingRule.query;
      });
    }
    return true;
  };

  scheduleRuleDialogOnDelete = (schedulingRule?: ISchedulingRuleDialogData) => {
    if (this.schedulingRule && this.schedulingRule.id === schedulingRule?.id) {
      this.schedulingRule = undefined;
    }
    return true;
  };

  handleEmployeeSearchChange = async () => {
    freeze(() => {
      this.scheduleRuleQuery = this.createEmployeeSearchScheduleRuleQuery();
    });
  };

  handleSchedulingRuleFieldChange = async () => {
    if (!this.schedulingRule) {
      this.scheduleRuleQuery = undefined;
      return;
    }
    const schedulingRuleReturnSchema = `
      id
      name
      options
      query
    `;
    const result           = await DataQueries.getViewsByIds(schedulingRuleReturnSchema, [this.schedulingRule.id]);
    const schedulingRule   = result?.[0];
    this.scheduleRuleQuery = schedulingRule?.query;
  };

  async setScheduleRuleWithDefault() {
    if (!isNullOrUndefinedOrEmpty(this.employeeSearch) || this.schedulingRule || !this.scheduleRuleContext) {
      return;
    }
    const scheduleRuleDefault = (await DataQueries.getScheduleRuleDefaults(this.scheduleRuleContext))?.[0];
    if (scheduleRuleDefault) {
      this.schedulingRule = scheduleRuleDefault;
    }
  }

  createEmployeeSearchScheduleRuleQuery() {
    const filters: ScheduleFilterInput = {};
    const searchEmployee = this.employeeSearch;
    if (searchEmployee) {
      filters.custom = [
        {'field': 'employee.id', eq: searchEmployee.id}
      ];
    }

    return getScheduleRuleQuery(this.autoSchedulePropertyMap, filters);
  }
}

const infoPanelShiftSchedulingHeaderViewModel = new InfoPanelShiftSchedulingHeaderViewModel();
export default infoPanelShiftSchedulingHeaderViewModel;
